"use client";

import React from "react";
import { Compass, CreditCard, MessageCircle, Sparkles } from "lucide-react";
import {
  FaFacebookF,
  FaInstagram,
  FaTiktok,
  FaYoutube,
  FaWhatsapp,
  FaLinkedinIn,
} from "react-icons/fa";

export default function SupportPage() {
  const supportItems = [
    { icon: Compass, text: "Trip planning and booking support" },
    { icon: CreditCard, text: "Payments, billing, and refunds" },
    {
      icon: Sparkles,
      text: "Booking updates, cancellations, and rescheduling)",
    },
    { icon: MessageCircle, text: "Direct chat support with our local team" },
    {
      icon: Compass,
      text: "Travel guidance, safety tips, and destination insights",
    },
  ];

  return (
    <div className="min-h-screen bg-[#faf8f5] dark:bg-[#0a0a0a] pt-16 md:pt-20">
      {" "}
      {/* FULL-WIDTH CREATIVE HEADER with Sri Lanka image */}
      <header className="relative w-full overflow-hidden border-b border-gray-200 dark:border-gray-700">
        {/* Background image (internet URL) */}
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{
            backgroundImage:
              "url('https://lilylanka-public-prod.sgp1.cdn.digitaloceanspaces.com/Site/images/background/support_background.jpg')",
            // Make image pop
            filter: "contrast(1.1) saturate(1.15)",
            transform: "scale(1.03)", // avoids edge gaps
          }}
        />

        {/* Stronger, clearer overlay (less white wash) */}
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/20 via-black/10 to-indigo-900/20" />

        {/* Decorative shapes */}
        <div className="pointer-events-none absolute -top-24 -left-28 h-80 w-80 rounded-full bg-purple-300/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 -right-28 h-96 w-96 rounded-full bg-indigo-300/20 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-24">
          <div className="flex items-center justify-between gap-6">
            {/* Left: Title + Tagline */}
            <div className="flex items-start gap-4">
              <div>
                <h1 className="text-4xl md:text-4xl font-extrabold text-white tracking-tight drop-shadow-sm">
                  Support
                </h1>

                <p className="mt-2 text-base md:text-lg text-white/90 max-w-xl drop-shadow-sm">
                  We’re here 24/7 to help with bookings, payments, partners, and
                  anything LilyLanka.
                </p>

                <div className="mt-4 h-1 w-24 rounded-full bg-white/90" />
              </div>
            </div>
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
      </header>
      {/* Main */}
      <div className="relative max-w-6xl mx-auto px-6 py-14">
        <div className="grid gap-10 lg:grid-cols-12 items-start">
          {/* Left: Hero / Intro */}
          <div className="lg:col-span-7">
            <div className="rounded-3xl bg-white/70 dark:bg-[#18181b]/80 backdrop-blur-md border border-white/60 dark:border-gray-700 shadow-[0_10px_30px_rgba(0,0,0,0.06)] overflow-hidden">
              <div className="p-8 md:p-10">
                {/* TWO CONTENTS: same as before, but second is smaller */}
                <div className="space-y-6">
                  {/* First content (keep size as you said OK) */}
                  <h2 className="text-3xl md:text-2xl font-bold text-gray-900 dark:text-white leading-tight">
                    "Welcome to Lily Support Center,
                    <br />
                    Dedicated space for seamless assistance and thoughtful
                    guidance
                    <br />
                    throughout your journey."
                  </h2>

                  {/* Second content (smaller font) */}
                  <p className="text-base md:text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    Our support team is here to ensure your experience is
                    smooth, enjoyable, and stress free, whether you are planning
                    ahead or already exploring the island.
                  </p>
                </div>

                {/* Message Box */}
                <div className="bg-gray-100/80 dark:bg-[#27272a] border border-gray-200/60 dark:border-gray-700 rounded-2xl p-6 mt-8 text-center">
                  <p className="text-base text-gray-700 dark:text-gray-300">
                    If something requires attention, we are here to help. If
                    everything exceeds expectations, we would love to hear about
                    it.
                  </p>
                </div>
              </div>

              <div className="h-2 w-full bg-gradient-to-r from-purple-600 via-fuchsia-600 to-indigo-600" />
            </div>
          </div>

          {/* Right: Support Items + Hours */}
          <div className="lg:col-span-5">
            <div className="rounded-3xl bg-white/70 dark:bg-[#18181b]/80 backdrop-blur-md border border-white/60 dark:border-gray-700 shadow-[0_10px_30px_rgba(0,0,0,0.06)] overflow-hidden">
              <div className="p-8 md:p-10">
                {/* Support Items List */}
                <div className="space-y-4 mb-10">
                  {supportItems.map((item, idx) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={idx}
                        className="group flex items-start gap-4 rounded-2xl border border-gray-200/70 dark:border-gray-700 bg-white/60 dark:bg-[#27272a]/60 p-4 transition hover:bg-white dark:hover:bg-[#27272a] hover:shadow-sm"
                      >
                        <div className="relative flex-shrink-0">
                          <div className="absolute -inset-1 rounded-full bg-purple-600/20 blur-sm opacity-0 group-hover:opacity-100 transition" />
                          <div className="relative bg-purple-600 rounded-full p-2 flex-shrink-0 shadow-sm">
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                        </div>

                        <span className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                          {item.text}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Hours */}
                <div className="rounded-2xl border border-gray-200/70 dark:border-gray-700 bg-gradient-to-br from-white/70 to-gray-50/70 dark:from-[#27272a] dark:to-[#27272a] p-6 text-center">
                  <p className="text-base font-semibold text-gray-900 dark:text-white">
                    We're Online Monday – Sunday | 9:00 AM – 6:00 PM (Sri Lanka
                    Time)
                  </p>

                  <div className="mt-5">
                    <p className="text-base text-gray-700 dark:text-gray-300">
                      For faster assistance, please contact us via WhatsApp. Our
                      team responds promptly and personally to ensure you are
                      always supported.
                    </p>
                  </div>
                </div>
              </div>

              <div className="h-2 w-full bg-gradient-to-r from-indigo-600 via-purple-600 to-fuchsia-600" />
            </div>

            {/* Keep hidden (no content changes) */}
            <div className="mt-8 hidden">
              <div className="flex justify-center gap-3">
                <SocialIcon href="#">
                  <FaFacebookF className="text-white" />
                </SocialIcon>
                <SocialIcon href="#">
                  <FaInstagram className="text-white" />
                </SocialIcon>
                <SocialIcon href="#">
                  <FaTiktok className="text-white" />
                </SocialIcon>
                <SocialIcon href="#">
                  <FaYoutube className="text-white" />
                </SocialIcon>
                <SocialIcon href="#">
                  <FaWhatsapp className="text-white" />
                </SocialIcon>
                <SocialIcon href="#">
                  <FaLinkedinIn className="text-white" />
                </SocialIcon>
              </div>
            </div>
          </div>
        </div>

        <div className="h-10" />
      </div>
    </div>
  );
}

/* Reusable components */
function SocialIcon({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="w-9 h-9 flex items-center justify-center bg-purple-800 rounded-full hover:bg-purple-700 transition text-base"
    >
      {children}
    </a>
  );
}
